import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Plus, X, Trash2 } from "lucide-react";

/**
 * AnchoredScriptView — renders script content with text-selection-based shot anchors.
 * - Directors drag-select text → floating + button appears
 * - Clicking + creates a shot anchor (highlighted range)
 * - Click anchor to select it → side panel shows shots
 * - Hover highlights, click persists highlight
 */
export default function AnchoredScriptView({
  content,
  heading,
  sceneNumber,
  projectId,
  anchors = [],
  selectedAnchorId,
  onAnchorSelect,
  onAnchorCreate,
  onAnchorDelete,
}) {
  const [selection, setSelection] = useState(null); // { text, startOffset, endOffset, rect }
  const [hoveredAnchorId, setHoveredAnchorId] = useState(null);
  const containerRef = useRef(null);

  // Handle text selection
  const handleMouseUp = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      return;
    }

    const text = sel.toString().trim();
    if (text.length < 3) return;

    // Get position relative to container
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    if (!containerRect) return;

    // Find offset within the full content
    const contentText = content || "";
    const startSearch = contentText.toLowerCase().indexOf(text.toLowerCase().slice(0, 40));
    const startOffset = startSearch >= 0 ? startSearch : 0;
    const endOffset = startOffset + text.length;

    setSelection({
      text,
      startOffset,
      endOffset,
      top: rect.bottom - containerRect.top + containerRef.current.scrollTop + 4,
      left: rect.left - containerRect.left + rect.width / 2,
    });
  }, [content]);

  // Clear selection when clicking outside
  const handleClickOutside = useCallback((e) => {
    if (e.target.closest("[data-anchor-btn]") || e.target.closest("[data-anchor-highlight]")) return;
    // Don't clear if user just finished selecting text
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;
    setSelection(null);
  }, []);

  const handleCreateAnchor = () => {
    if (!selection) return;
    onAnchorCreate?.({
      selected_text: selection.text,
      start_offset: selection.startOffset,
      end_offset: selection.endOffset,
    });
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // Render content with anchor highlights
  const renderContent = () => {
    if (!content) return null;

    const lines = content.split("\n");
    let globalOffset = 0;

    return lines.map((line, lineIdx) => {
      const lineStart = globalOffset;
      const lineEnd = globalOffset + line.length;
      globalOffset += line.length + 1; // +1 for \n

      // Determine line type for screenplay formatting
      const trimmed = line.trim();
      const isHeading = trimmed.match(/^(INT\.|EXT\.|INT\/EXT\.)/i);
      const isCharName = trimmed === trimmed.toUpperCase() && trimmed.length > 1 && trimmed.length < 40 && !isHeading;
      const isParenthetical = trimmed.startsWith("(") && trimmed.endsWith(")");
      const isTransition = trimmed.match(/^(CUT TO|FADE|DISSOLVE|SMASH CUT)/i);

      // Find anchors that overlap this line
      const lineAnchors = anchors.filter(a => {
        const aStart = a.start_offset || 0;
        const aEnd = a.end_offset || (aStart + (a.selected_text || "").length);
        return aStart < lineEnd && aEnd > lineStart;
      });

      const isHighlighted = lineAnchors.some(a => a.id === selectedAnchorId);
      const isHovered = lineAnchors.some(a => a.id === hoveredAnchorId);
      const lineAnchor = lineAnchors[0]; // Primary anchor for this line

      return (
        <div
          key={lineIdx}
          className={`relative px-2 py-0.5 rounded transition-all duration-200 cursor-text
            ${isHeading ? 'font-bold text-[var(--text-primary)] text-xs uppercase mt-6 mb-2 bg-[var(--bg-muted)]/50 py-2 px-4 rounded-lg' : ''}
            ${isCharName ? 'text-center font-semibold text-[var(--color-gold-400)] mt-4 mb-0.5 text-xs uppercase tracking-wider' : ''}
            ${isParenthetical ? 'text-center italic text-[var(--text-muted)] text-xs' : ''}
            ${isTransition ? 'text-right font-medium text-[var(--text-secondary)] uppercase text-xs mt-3' : ''}
            ${!isHeading && !isCharName && !isParenthetical && !isTransition ? 'text-[var(--text-secondary)] text-xs leading-relaxed' : ''}
            ${isHighlighted ? 'bg-[var(--color-gold-500)]/15 border-l-2 border-[var(--color-gold-500)]' : ''}
            ${isHovered && !isHighlighted ? 'bg-[var(--color-gold-500)]/8' : ''}
          `}
          onMouseEnter={() => lineAnchor && setHoveredAnchorId(lineAnchor.id)}
          onMouseLeave={() => setHoveredAnchorId(null)}
          onClick={(e) => {
            if (lineAnchor && !window.getSelection()?.toString()) {
              e.stopPropagation();
              onAnchorSelect?.(lineAnchor.id);
            }
          }}
          data-anchor-highlight={lineAnchor ? "true" : undefined}
        >
          {/* Anchor indicator dot */}
          {lineAnchor && lineAnchors.indexOf(lineAnchor) === 0 && lineStart <= (lineAnchor.start_offset || 0) + 5 && (
            <div className="absolute -left-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <div className={`w-2 h-2 rounded-full transition-all ${
                isHighlighted ? 'bg-[var(--color-gold-500)] scale-125' : 'bg-[var(--color-gold-500)]/40'
              }`} />
            </div>
          )}

          {/* Delete anchor button on hover */}
          {lineAnchor && (isHighlighted || isHovered) && lineStart <= (lineAnchor.start_offset || 0) + 5 && (
            <button
              onClick={(e) => { e.stopPropagation(); onAnchorDelete?.(lineAnchor.id); }}
              className="absolute -right-1 top-0 p-0.5 rounded bg-red-500/20 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500/40 transition-all z-10"
              style={{ opacity: isHovered || isHighlighted ? 1 : 0 }}
              title="Delete this anchor"
              data-testid="delete-anchor-btn"
            >
              <X size={10} />
            </button>
          )}

          {/* Shot count badge */}
          {lineAnchor && lineAnchor.shots?.length > 0 && lineStart <= (lineAnchor.start_offset || 0) + 5 && (
            <span className="absolute -right-1 -top-1 text-[7px] px-1 py-0 rounded-full bg-[var(--color-gold-500)] text-black font-bold min-w-[14px] text-center">
              {lineAnchor.shots.length}
            </span>
          )}

          {trimmed || <span>&nbsp;</span>}
        </div>
      );
    });
  };

  return (
    <div
      ref={containerRef}
      className="relative h-full overflow-y-auto select-text px-6 py-4"
      onMouseUp={handleMouseUp}
      onClick={handleClickOutside}
      data-testid="anchored-script-view"
    >
      {/* Scene heading */}
      {heading && (
        <div className="text-center mb-6">
          <h3 className="text-base font-bold text-[var(--text-primary)] uppercase tracking-wide">{heading}</h3>
        </div>
      )}

      {/* Script content with highlights */}
      <div className="font-mono max-w-2xl mx-auto pl-4">
        {renderContent()}
      </div>

      {/* Floating + button on text selection */}
      <AnimatePresence>
        {selection && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute z-50"
            style={{ top: selection.top, left: selection.left, transform: "translateX(-50%)" }}
            data-anchor-btn="true"
          >
            <button
              onClick={handleCreateAnchor}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--color-gold-500)] text-black text-xs font-semibold shadow-lg hover:bg-[var(--color-gold-400)] transition-all"
              data-testid="create-anchor-btn"
            >
              <Plus size={14} />
              <span>Add Shot</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
